import { MarkdownToJSX } from 'markdown-to-jsx'
import { Children, cloneElement, isValidElement } from 'react'

import type { CSSProperties, PropsWithChildren } from 'react'

/**
 * Notes:
 * - `@turbodocx/html-to-docx` has no CSS cascade engine so the export must
 * include inline styles instead. The docx-export snapshot test guards
 * against drift.
 *
 * - Unit note: html-to-docx mishandles `rem` and ignores table cell `padding`,
 *  so use px/pt only and rely on Word's default cell margins for table cells.
 *
 * - html-to-docx bolds/italicizes `strong`/`em` tags natively.
 *
 * - html-to-docx collapses the whole `blockquote` quote into a single paragraph
 * and drops any lists or extra paragraphs inside it. `DocxBlockquote` therefore
 * emits a tag the converter does not recognise to retain visual formatting.
 */

// Plain table for export
const DocxTable = ({ children }: PropsWithChildren) => (
  <table style={{ borderCollapse: 'collapse', width: '100%' }}>{children}</table>
)

const TH_STYLE: CSSProperties = {
  backgroundColor: '#f3f4f6',
  border: '1px solid #d1d5db',
  fontWeight: 600,
  textAlign: 'left',
}

const TD_STYLE: CSSProperties = { border: '1px solid #d1d5db', verticalAlign: 'top' }

// Without a block element wrapper, html-to-docx starts a new paragraph at every
// inline child (e.g. `<sup>`s), stacking them on separate lines
const DocxTh = ({ children }: PropsWithChildren) => (
  <th style={TH_STYLE}><p style={{ margin: 0 }}>{children}</p></th>
)

const DocxTd = ({ children }: PropsWithChildren) => (
  <td style={TD_STYLE}><p style={{ margin: 0 }}>{children}</p></td>
)

const BLOCKQUOTE_STYLE: CSSProperties = {
  borderLeft: '4px solid #e5e7eb',
  color: '#6b7280',
  fontStyle: 'italic',
  margin: '8px 0',
  marginLeft: '20px',
  paddingLeft: '16px',
}

const DocxBlockquote = ({ children }: PropsWithChildren) => (
  <div>
    {Children.map(children, child =>
      isValidElement<{ style?: CSSProperties }>(child)
        ? cloneElement(child, { style: { ...BLOCKQUOTE_STYLE, ...child.props.style } })
        : child,
    )}
  </div>
)

export const docxComponents = {
  a: {
    component: 'a',
    props: {
      style: { color: '#06c', textDecoration: 'underline' },
    },
  },
  blockquote: {
    component: DocxBlockquote,
  },
  code: {
    component: 'code',
    props: {
      style: {
        backgroundColor: '#f3f4f6',
        fontFamily: 'Courier New, monospace',
        padding: '2px 4px',
      },
    },
  },
  hr: {
    component: 'hr',
    props: {
      style: { border: 0, borderTop: '1px solid #e5e7eb', margin: '24px 0' },
    },
  },
  pre: {
    component: 'pre',
    props: {
      style: { backgroundColor: '#f3f4f6', margin: '16px 0', padding: '16px' },
    },
  },
  sup: {
    component: 'sup',
    props: {
      // html-to-docx's `w:vertAlign="superscript"` shrinks text
      style: { color: '#06c', verticalAlign: 'super' },
    },
  },
  table: {
    component: DocxTable,
  },
  td: {
    component: DocxTd,
  },
  th: {
    component: DocxTh,
  },
} satisfies MarkdownToJSX.Overrides
